import React, {
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Selectors from '../../../../selectors'
import _ from 'lodash'
import Form from '../..'
import Actions from '../../../../actions'
import Operations from '../../../../operations'
import { colors } from '@lmc/common/constants'
import { getNominative } from '../../../../common/utils'
import { ErrorBox, Spinner, IconSectionHeader } from '../../../generic'
import Field from '../../../fields'
import RiskAssessmentOutcome from './RiskAssessmentOutcome'
import {
  AssessmentEditableHeader,
  FormProps
} from '../../../modals/CreateRiskAssessment/components'
import FormFields from '../../FormFields'
import { withRouter } from '../../../../withRouter'
import MultiSelectField from '../../../fields/types/MultiSelectField'
import { Text } from '@lmc/ui-kit'
import { ImageSection } from '../../../generic/ListItem/components'
import { Modal } from '@lmc/ui-kit'
import RiskAssessmentEditFooter from 'screens/Residents/screens/ResidentRiskAssessmentsScreens/components/RiskAssessmentEditFooter'
import { useTranslation } from 'react-i18next'
import { riskAssessmentFormConstants } from 'locale/riskAssessmentForm'
import { NAMESPACE } from 'locale'
import { RouterProps } from '@lmc/common/types/router'
import {
  CarePlan,
  CarePlanAdl,
  FormField,
  HomeFeatures,
  RiskAssessment
} from '@lmc/ts-common'

interface Form {
  id: string
  name: string
  iconText?: string
  color?: string
  hasRiskScore?: boolean
  hasActions?: boolean
  formFields: FormField[]
  carePlanAdl?: CarePlanAdl
}

interface FormData {
  carePlanAdl: string | null
  name?: string | null
  [key: string]: any
}

interface CarePlanAdlExtended extends Omit<CarePlanAdl, 'icon'> {
  noFurtherSupportRequired: boolean
  value: any
  carePlan?: CarePlan
  icon: string
  iconText: string
}

export interface RefactoredRiskAssessmentFormProps {
  fetchList: (list: string) => void
  carePlanAdls: CarePlanAdlExtended[]
  loadListItem: (list: string, itemId: string) => void
  form?: Form
  form_id: string
  fetchCarePlan: (id: string) => void
  carePlansDefault: CarePlanAdlExtended[]
  features: HomeFeatures
  router: RouterProps<{
    id: string
    form_id?: string
    risk_assessment_id?: string
  }>
  addForm: (initial?: any) => void
  customName?: string
  formData: FormData
  setFormField: (data: { key: string; value: any }) => void
  fields: Record<string, any>
  carePlans?: {
    adls: CarePlanAdlExtended[]
    carePlanSortType: number | null
    historyDateRange: {
      startDate: Date
      endDate: Date
    }
    riskAssessmentSortType: number | null
  }
  carePlanNominative: string
  resident?: {
    name: {
      first: string
      last: string
    }
    dateOfBirth: string
    picture: string
    status: string
  }
  risk: {
    level: 'na' | 'low' | 'medium' | 'high' | 'severe' | 'incomplete'
    score: number
  }
  createItem: (data: any) => void
  onCancel: () => void
  onComplete: (result: any) => void
  initial: Record<string, any>
  riskAssessmentId?: string
  updateItem: (data: any) => void
  errors: any
  archived: boolean
  style?: React.CSSProperties
}

const HeaderInner = styled.div<{ $hasIconText: boolean }>`
  margin-left: ${(props) => (props.$hasIconText ? 0 : 55)}px;
  margin-bottom: 24px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-left: 0;
  }
`

const StyledErrorBox = styled(ErrorBox)`
  margin-left: 55px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-left: 0;
  }
`

const CarePlanSelectorWrapper = styled.div`
  margin-top: 10px;
  background: ${colors.white};
  border-radius: 15px;
  padding: 15px;
  display: flex;
`

const WarningText = styled(Text)`
  color: ${colors.red};
`

const SectionContainer = styled.div``

const TitleContainer = styled.div`
  margin-top: 55px;
`

const FieldContainer = styled.div`
  padding-top: 24px;
  margin-left: 0;
`

const CarePlanOptionWrapper = styled.div`
  align-items: center;
  display: flex;
`

const CarePlanName = styled.span``

const ModalNoteText = styled(Text)`
  color: ${colors.red};
`

const ModalDescriptionText = styled(Text)`
  margin-bottom: 20px;
`

const ModalCarePlanList = styled.div`
  margin-top: 10px;
`

const CarePlanItem = styled.div`
  display: flex;
`

const CarePlanItemText = styled(Text)`
  margin-left: 5px;
`

export const RefactoredRiskAssessmentForm = (
  props: RefactoredRiskAssessmentFormProps
) => {
  const {
    fetchList,
    carePlanAdls,
    loadListItem,
    form,
    form_id,
    fetchCarePlan,
    carePlansDefault,
    features,
    router,
    addForm,
    customName,
    formData,
    setFormField,
    fields,
    carePlans,
    carePlanNominative,
    resident,
    risk,
    createItem,
    onCancel,
    onComplete,
    initial,
    riskAssessmentId,
    updateItem,
    errors,
    archived,
    style
  } = props
  const [carePlansState, setCarePlansState] = useState<CarePlanAdlExtended[]>(
    []
  )
  const [carePlansDefaultState, setCarePlansDefaultState] = useState<
    CarePlanAdlExtended[]
  >([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)
  const hasInitializedForm = useRef(false)
  const hasInitializedCarePlans = useRef(false)

  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS_FORM)

  // Initialize form on mount
  useEffect(() => {
    if (!carePlanAdls) {
      fetchList('care-plan-adls')
    }

    loadListItem('forms', form_id)

    const id = router?.params?.id

    if (features && id) {
      fetchCarePlan(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize care plans from defaults (only once when carePlansDefault is available)
  useEffect(() => {
    if (carePlansDefault && !hasInitializedCarePlans.current) {
      const carePlansDefaultWithSupport = carePlansDefault.filter(
        (carePlan) => !carePlan.noFurtherSupportRequired
      )
      if (carePlansDefaultWithSupport.length > 0) {
        hasInitializedCarePlans.current = true
        setCarePlansState(carePlansDefaultWithSupport)
        setCarePlansDefaultState(carePlansDefaultWithSupport)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carePlansDefault])

  // Add form when available (only once)
  useEffect(() => {
    if (form && !hasInitializedForm.current) {
      hasInitializedForm.current = true
      addForm({
        ...formData,
        carePlanAdl: form.carePlanAdl || null
      })
      setFormField({ key: 'name', value: customName || null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  const hasChangedCarePlans = useCallback(() => {
    // Create sorted copies without mutating original arrays
    const defaultSorted = [...(carePlansDefaultState || [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )
    const currentSorted = [...(carePlansState || [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )
    return JSON.stringify(defaultSorted) !== JSON.stringify(currentSorted)
  }, [carePlansDefaultState, carePlansState])

  const handleSubmit = useCallback(() => {
    const { params } = router

    if (!_.isEqual(fields, initial) || hasChangedCarePlans()) {
      const chosenId = form_id || params.form_id

      const name = fields?.name
        ? `${form?.name} for ${fields?.name}`
        : undefined

      const carePlansChoosen = !!carePlansState.length

      createItem({
        url: `list/risk-assessments`,
        json: {
          form: chosenId,
          formData: fields,
          name,
          resident: params.id,
          riskLevel: risk.level,
          riskScore: risk.score,
          carePlans: carePlansChoosen
            ? carePlansState?.map((item) => item?.value || item?.id)
            : []
        },
        formId: FORM_ID,
        onError: (e: Error) => {
          console.log('onError', e)
          formRef.current?.scrollIntoView({ behavior: 'smooth' })
        },
        onComplete: (res: RiskAssessment) => {
          if (!params?.risk_assessment_id) {
            onComplete(res)
          } else {
            router.navigate(
              `/residents/${params.id}/risk-assessments/${res.id}`,
              { replace: true }
            )
          }
        }
      })
    } else if (riskAssessmentId) {
      updateItem({
        url: `list/risk-assessments/${riskAssessmentId}`,
        formId: FORM_ID,
        onError: (e: Error) => {
          console.log('onError', e)
          formRef.current?.scrollIntoView({ behavior: 'smooth' })
        },
        onComplete: (res: RiskAssessment) => {
          if (!params?.risk_assessment_id) {
            onComplete(res)
          } else {
            router.navigate(
              `/residents/${params.id}/risk-assessments/${res.id}`,
              { replace: true }
            )
          }
        }
      })
    } else {
      onCancel()
    }
  }, [
    fields,
    initial,
    hasChangedCarePlans,
    carePlansState,
    form_id,
    router,
    risk,
    createItem,
    updateItem,
    riskAssessmentId,
    onComplete,
    onCancel,
    form
  ])

  const setCustomName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormField({
        key: 'name',
        value: e.target.value
      })
    },
    [setFormField]
  )

  const handleCarePlanIds = useCallback((value: CarePlanAdlExtended[]) => {
    setCarePlansState(value)
  }, [])

  const carePlanSelectOption = useCallback(
    ({ name, icon, color }: { name: string; icon: string; color: string }) => (
      <CarePlanOptionWrapper>
        <ImageSection popup iconColor={color} icon={icon} />
        <CarePlanName>{name}</CarePlanName>
      </CarePlanOptionWrapper>
    ),
    []
  )

  const renderFields = () => {
    const carePlansAdlsSelection = carePlans?.adls || carePlanAdls || []

    const carePlanOptions = carePlansAdlsSelection
      ?.filter(
        (item) =>
          item?.carePlan &&
          !item?.carePlan?.noFurtherSupportRequired &&
          item?.carePlan?.nextReviewDate
      )
      .map((item) => ({
        id: item.carePlan?.id,
        name: item.name,
        icon: item?.icon,
        color: item?.color
      }))

    if (!form || !form.formFields) return null

    const carePlansChoosen = carePlansState?.length
    const carePlansOptios = carePlanOptions?.length

    const attachedToTranslation = t(riskAssessmentFormConstants.ATTACHED_TO, {
      carePlanNominative: getNominative(
        carePlanNominative,
        riskAssessmentFormConstants.CARE_PLANS
      )
    })

    return (
      <>
        <CarePlanSelectorWrapper>
          <MultiSelectField
            formKey={undefined}
            label={attachedToTranslation}
            customOnChange={handleCarePlanIds}
            onChange={() => {}}
            options={carePlanOptions || []}
            formatOptionLabel={carePlanSelectOption}
            customValues={carePlansState}
            centerLabel
            errors={undefined}
            style={null}
            placeholder={''}
            values={[]}
          />
        </CarePlanSelectorWrapper>
        {carePlansOptios && !carePlansChoosen ? (
          <WarningText as="p3">
            {passPortNote(carePlanNominative, t)}
          </WarningText>
        ) : null}
        {!carePlansOptios && !carePlansChoosen ? (
          <WarningText as="p3">
            {reviewCarePlanNote(carePlanNominative, t)}
          </WarningText>
        ) : null}
        <FormFields
          form={form}
          fields={fields}
          readOnly={false}
          userGenerated
        />
      </>
    )
  }

  const renderOutcomesScore = () => {
    if (form && !form.hasRiskScore) return null
    const riskOutcomeScoreTranslation = t(
      riskAssessmentFormConstants.RISK_OUTCOME_SCORE
    )
    return (
      <SectionContainer>
        <TitleContainer>
          <IconSectionHeader
            icon="info"
            title={riskOutcomeScoreTranslation}
            color={form?.color || colors.darkblue}
          />
        </TitleContainer>
        <FieldContainer>
          <RiskAssessmentOutcome
            hasActions={form?.hasActions}
            resident={resident!}
            risk={risk}
          />
        </FieldContainer>
      </SectionContainer>
    )
  }

  const renderActionsSection = () => {
    if (form && !form.hasActions) return null
    const actionsToMitigateTranslation = t(
      riskAssessmentFormConstants.ACTIONS_TO_MITIGATE
    )
    return (
      <SectionContainer>
        <TitleContainer>
          <IconSectionHeader
            icon="actions"
            title={actionsToMitigateTranslation}
            color={form?.color || colors.darkblue}
          />
        </TitleContainer>
        <FieldContainer>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              className="lmc-card-edit"
              keys="actions"
              formId={FORM_ID}
              note={getActionsNote(carePlanNominative, t)}
              layout="no-label"
              required
              type="textarea"
              values={fields}
            />
          </Suspense>
        </FieldContainer>
      </SectionContainer>
    )
  }

  const renderHeader = () => {
    return (
      <div>
        <HeaderInner $hasIconText={!!form?.iconText}>
          {fields && (fields.name || fields.name === '') ? (
            <AssessmentEditableHeader
              form={form as FormProps}
              hasToggle
              onChange={setCustomName}
              value={fields.name}
            />
          ) : (
            <IconSectionHeader
              isMainHeader
              title={form?.name}
              iconText={form?.iconText}
              color={form?.color}
            />
          )}
        </HeaderInner>
        <StyledErrorBox errors={errors} />
      </div>
    )
  }

  const renderModal = () => {
    const carePlansToDisplay = carePlansState.filter(
      (carePlan) => !carePlan.noFurtherSupportRequired
    )
    const noCarePlansToDisplay = carePlansState?.length
    const confirmTextTranslation = t(riskAssessmentFormConstants.CONFIRM_TEXT)
    const cancelTextTranslation = t(riskAssessmentFormConstants.CANCEL_TEXT)
    const pleaseNoteTranslation = t(riskAssessmentFormConstants.PLEASE_NOTE)
    const updatingRiskAssessmentTranslation = t(
      riskAssessmentFormConstants.UPDATING_RISK_ASSESSMENT,
      {
        carePlanNominative: getNominative(
          carePlanNominative,
          `${noCarePlansToDisplay > 1 ? riskAssessmentFormConstants.CARE_PLANS : riskAssessmentFormConstants.CARE_PLAN}`
        )
      }
    )

    return (
      <Modal
        localisation={{
          confirmText: confirmTextTranslation,
          cancelText: cancelTextTranslation
        }}
        onConfirm={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        isModalVisible={isModalVisible}
        setIsModalVisible={() => setIsModalVisible(!isModalVisible)}
      >
        <>
          <ModalNoteText as="h5">{pleaseNoteTranslation}:</ModalNoteText>
          <ModalDescriptionText as="p2">
            {updatingRiskAssessmentTranslation}
          </ModalDescriptionText>
          <ModalCarePlanList>
            {!!noCarePlansToDisplay &&
              carePlansToDisplay?.map((carePlan) => (
                <CarePlanItem key={carePlan.id}>
                  <ImageSection
                    popup
                    icon={carePlan?.icon}
                    iconColor={carePlan?.color}
                    iconText={carePlan?.iconText}
                  />
                  <CarePlanItemText as="h5">{carePlan?.name}</CarePlanItemText>
                </CarePlanItem>
              ))}
          </ModalCarePlanList>
        </>
      </Modal>
    )
  }

  const handleSave = useCallback(() => {
    const carePlansChoosen = carePlansState?.length

    if (
      (!!carePlansChoosen && !_.isEqual(fields, initial)) ||
      (hasChangedCarePlans() && !!carePlansChoosen)
    ) {
      setIsModalVisible(true)
    } else {
      handleSubmit()
    }
  }, [fields, initial, carePlansState, hasChangedCarePlans, handleSubmit])
  if (!carePlanAdls?.length || !fields || !form || !resident) {
    return <Spinner />
  }

  return (
    <div
      data-testid="lmc-risk-assessment-form"
      id="lmc-risk-assessment-form"
      ref={formRef}
      style={style}
    >
      {renderHeader()}
      {renderFields()}
      {renderOutcomesScore()}
      {renderActionsSection()}
      {renderModal()}
      <RiskAssessmentEditFooter
        onCancel={onCancel}
        onSave={handleSave}
        isLoading={false}
        isEditing={!!riskAssessmentId}
        riskAssessmentId={riskAssessmentId}
        residentId={router.params.id}
        archived={archived}
      />
    </div>
  )
}

const FORM_ID = 'createRiskAssessment'
const getActionsNote = (
  carePlanNominative: string,
  t: (key: string, options?: Record<string, unknown>) => string
) =>
  t(riskAssessmentFormConstants.ACTIONS_NOTE, {
    carePlanNominative: getNominative(
      carePlanNominative,
      riskAssessmentFormConstants.CARE_PLAN
    )
  })

const passPortNote = (
  carePlanNominative: string,
  t: (key: string, options?: Record<string, unknown>) => string
) =>
  t(riskAssessmentFormConstants.PASSPORT_NOTE, {
    carePlanNominative: getNominative(
      carePlanNominative,
      riskAssessmentFormConstants.CARE_PLAN
    )
  })

const reviewCarePlanNote = (
  carePlanNominative: string,
  t: (key: string, options?: Record<string, unknown>) => string
) =>
  t(riskAssessmentFormConstants.REVIEW_CARE_PLAN_NOTE, {
    carePlanNominative: getNominative(
      carePlanNominative,
      riskAssessmentFormConstants.CARE_PLAN
    )
  })

const mapStateToProps = (state: any, props: { router: RouterProps }) => {
  const residentId = props.router.params.id
  return {
    form: (
      Selectors.formatFieldsForAssessment as (
        state: any,
        props: { router: RouterProps }
      ) => Form
    )(state, props),
    resident: _.find(state.data.residents, { id: residentId }),
    carePlanAdls: state.data['care-plan-adls'],
    risk: (
      Selectors.calculateAssessmentRisk as (
        state: any,
        props: { router: RouterProps }
      ) => {
        level: 'na' | 'low' | 'medium' | 'high' | 'severe' | 'incomplete'
        score: number
      }
    )(state, props),
    carePlanNominative: state.global.home.preferredCarePlansNominative,
    carePlans: state.carePlans,
    features: state.global.home
  }
}

const mapDispatchToProps = (dispatch: Function) => ({
  fetchCarePlan: (id: string) => dispatch(Operations.fetchResidentAdls(id)),
  loadListItem: (list_id: string, item_id: string) =>
    dispatch(Operations.loadListItem(list_id, item_id)),
  addForm: (initial = {}) =>
    dispatch(
      Actions.addForm({
        formId: FORM_ID,
        initial
      })
    )
})

const wrappedForm = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RefactoredRiskAssessmentForm)
)

export default Form(wrappedForm, FORM_ID)
